import type { StreamedCitation } from "@soulbyte/core";

/** One played turn: what we sent, what we expected, and the whole answer we got back. */
export type SimulateTurnResult = {
  user: string;
  expected?: string;
  actual: string;
  modelName?: string | null;
  totalTokens?: number | null;
  /** Human-readable tool/skill steps from response.status details, e.g. "Using X: list_stadtteile". */
  toolCalls: string[];
  /** RAG / GraphRAG citations surfaced during the turn. */
  evidence: StreamedCitation[];
  durationMs: number;
  errored: boolean;
};

/** One conversation is one dataset played into its own thread. */
export type SimulateConversationResult = {
  threadId: string;
  name?: string;
  turns: SimulateTurnResult[];
};

export type SimulateReportSummary = {
  totalTurns: number;
  erroredTurns: number;
  totalTokens: number;
  models: string[];
};

export type SimulateReport = {
  simulatedAt: string;
  assistantId: string;
  summary: SimulateReportSummary;
  conversations: SimulateConversationResult[];
};

/** summarizeConversations rolls every turn up into totals for the run. */
export function summarizeConversations(
  conversations: SimulateConversationResult[],
): SimulateReportSummary {
  const turns = conversations.flatMap((conversation) => conversation.turns);
  const totalTokens = turns.reduce(
    (sum, turn) => sum + (turn.totalTokens ?? 0),
    0,
  );
  const models = [
    ...new Set(
      turns
        .map((turn) => turn.modelName)
        .filter((model): model is string => Boolean(model)),
    ),
  ];
  return {
    totalTurns: turns.length,
    erroredTurns: turns.filter((turn) => turn.errored).length,
    totalTokens,
    models,
  };
}

export function buildReport(
  assistantId: string,
  conversations: SimulateConversationResult[],
): SimulateReport {
  return {
    simulatedAt: new Date().toISOString(),
    assistantId,
    summary: summarizeConversations(conversations),
    conversations,
  };
}

/** downloadReport turns the report into a JSON file the browser saves to disk. */
export function downloadReport(report: SimulateReport): void {
  const blob = new Blob([JSON.stringify(report, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `simulation-report-${report.simulatedAt.replace(/[:.]/g, "-")}.json`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
